import { useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { ISO_NUMERIC_TO_ALPHA2, countryName } from "../../data/countries";

import worldTopo from "world-atlas/countries-110m.json";

/**
 * The world, with the countries somebody has a client in picked out.
 *
 * The hover tooltip belongs to the map and nothing outside needs to know about
 * it, so it lives here. It is a sibling of the card rather than a child:
 * fixed positioning inside a container that hides its overflow is asking for
 * the tooltip to be clipped at the border.
 */
export default function WorldMap({ activeCodes, selectedCode, language, t, onSelect }) {
  const [hoverInfo, setHoverInfo] = useState(null);

  const handleClick = (geo) => {
    const numericId = String(geo.id || "").padStart(3, "0");
    const alpha2 = ISO_NUMERIC_TO_ALPHA2[numericId];
    if (!alpha2) return;
    // A tap on a touch screen fires the hover handlers and never fires the one
    // that clears them, so without this the tooltip stays on screen for good.
    setHoverInfo(null);
    onSelect(alpha2);
  };

  return (
    <>
      <div className="relative bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="trasferte-map">
        <ComposableMap
          projection="geoEquirectangular"
          projectionConfig={{ scale: 178 }}
          width={960}
          height={560}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <Geographies geography={worldTopo}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const numericId = String(geo.id || "").padStart(3, "0");
                const alpha2 = ISO_NUMERIC_TO_ALPHA2[numericId];
                const active = alpha2 && activeCodes.has(alpha2);
                const isSelected = alpha2 && alpha2 === selectedCode;
                // Falls back to the name inside the map file for the few
                // shapes with no ISO code of their own, rather than to a bare
                // two-letter code.
                const displayName = alpha2
                  ? countryName(alpha2, language)
                  : (geo.properties?.name || null);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    data-testid={alpha2 ? `country-${alpha2}` : undefined}
                    onClick={() => handleClick(geo)}
                    onMouseEnter={(e) => {
                      if (!displayName) return;
                      setHoverInfo({ name: displayName, x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => {
                      if (!displayName) return;
                      setHoverInfo({ name: displayName, x: e.clientX, y: e.clientY });
                    }}
                    onMouseLeave={() => setHoverInfo(null)}
                    style={{
                      default: {
                        fill: isSelected ? "#4338CA" : active ? "#818CF8" : "#E2E8F0",
                        stroke: "#FFFFFF",
                        strokeWidth: 0.5,
                        outline: "none",
                        cursor: alpha2 ? "pointer" : "default",
                      },
                      hover: {
                        fill: alpha2 ? (isSelected ? "#3730A3" : "#6366F1") : "#E2E8F0",
                        outline: "none",
                      },
                      pressed: { fill: "#3730A3", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
        <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-indigo-400 inline-block" />
            <span>{t("trasferte.legendWithTrip")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-slate-200 inline-block" />
            <span>{t("trasferte.legendNoTrip")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-indigo-700 inline-block" />
            <span>{t("trasferte.legendSelected")}</span>
          </div>
        </div>
      </div>

      {hoverInfo && (
        <div
          data-testid="trasferte-map-tooltip"
          className="pointer-events-none fixed z-50 px-2.5 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-md shadow-lg whitespace-nowrap"
          style={{ left: hoverInfo.x + 12, top: hoverInfo.y + 12 }}
        >
          {hoverInfo.name}
        </div>
      )}
    </>
  );
}
